import { cloneDeep, compact, map, orderBy } from 'lodash';
import { hasGeometry } from 'geometry/geojson/convert';
import { calculateOverlap } from 'geometry/geojson/intersect';
import { logError } from 'api/errors';
import { area } from '3rd-party-wrappers/turf';

/**
 * TODO - Replace/revise this function that was copied over from geotools. It's named weirdly ("Legacy") to keep it
 * from spreading.
 *
 * Will collect properties from all intersecting polygons between the match and
 * the underlying.
 *
 * General idea for this algorithm:
 *
 * 1. Loop through all features in the underlying GeoJSON
 * 2. Find any intersections with the `searchPolygon` param
 * 3. If any intersection is found, add the properties of the underlying feature
 *   to the result array. But only if the intersection is an actual polygon.
 *   We are skipping any multilinestrings that might be in common.
 * 4. Calculate the area of the intersection to get an understanding for how
 *   big that slice is.
 * 5. Return a complete list of all the properties collected.
 *
 * @param underlying {GeoJSON<FeatureCollection>} The underlying GeoJSON to
 *   intersect with. This is the param that contains the data to search for.
 * @param searchPolygon {GeoJSON} The polygon that defines the search area.
 * @param options Object
 * @param options.threshold (optional) The minimum area to consider valuable.
 *   Will remove anything with an area that's <= `threshold`.
 *   Default is 0.
 * @returns An array of properties that were found in the underlying data, based
 *   on the searchPolygon
 */
export const collectPropertiesLegacy = (underlying, searchPolygon, options) => {

  if (!underlying.features) {
    throw new Error('It seems the underlying data passed in is not a FeatureCollection');
  }
  options = options || {};
  const threshold = options.threshold || 0;

  const collectedProperties = map(underlying.features, function(feature) {
    let overlap;
    try {
      overlap = calculateOverlap(feature, searchPolygon, options.useLibraries);
    } catch(err) {
      logError({message:`Failed to get properties for intersection between, skipping.`});
    }

    if (hasGeometry(overlap)) {
      const overlapArea = area(overlap);

      // It's not a random LineString or MultiLineString thing and has got area
      if (overlapArea > threshold) {
        const props = cloneDeep(feature.properties);
        props.matchingArea = overlapArea;
        return props;
      }
    }
    return undefined;
  });

  return orderBy(compact(collectedProperties), ['matchingArea'], ['desc']);
};
